import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

import { apiClient } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'

interface IntelligenceSettingsData {
  behavior_simulation: {
    enabled: boolean
    default_profile: string
    response_delay_multiplier: number
    humanization_level: string
    engagement_threshold: number
  }
  spam_detection: {
    enabled: boolean
    sensitivity: string
    auto_block_threshold: number
    manual_review_threshold: number
    learning_mode: boolean
  }
  crisis_management: {
    enabled: boolean
    auto_response: boolean
    escalation_threshold: number
    notification_email: string
    response_timeout: number
  }
  performance: {
    api_timeout: number
    batch_size: number
    concurrent_requests: number
    cache_duration: number
  }
  privacy: {
    data_retention_days: number
    anonymize_logs: boolean
    user_consent_required: boolean
  }
}

const IntelligenceSettings: React.FC = () => {
  console.log('⚙️ IntelligenceSettings: Component mounting/rendering')

  const [hasChanges, setHasChanges] = useState(false)
  const [localSettings, setLocalSettings] = useState<IntelligenceSettingsData | null>(null)
  const queryClient = useQueryClient()

  const { data: settingsData, isLoading, error, status, fetchStatus } = useQuery({
    queryKey: ['intelligence-settings'],
    queryFn: async () => {
      console.log('🚀 INTELLIGENCE SETTINGS: Starting API call')
      console.log('🔗 INTELLIGENCE SETTINGS: URL will be /intelligence/settings')
      console.log('⏰ INTELLIGENCE SETTINGS: Timestamp:', new Date().toISOString())

      try {
        const result = await apiClient.get('/intelligence/settings')
        console.log('✅ INTELLIGENCE SETTINGS: API call successful')
        console.log('📊 INTELLIGENCE SETTINGS: Response data:', result)
        console.log('🔍 INTELLIGENCE SETTINGS: Response type:', typeof result)
        console.log('🔍 INTELLIGENCE SETTINGS: Response keys:', result ? Object.keys(result) : 'No keys')
        return result
      } catch (error) {
        console.error('❌ INTELLIGENCE SETTINGS: API call failed in queryFn')
        console.error('📋 INTELLIGENCE SETTINGS: Error object:', error)
        console.error('📋 INTELLIGENCE SETTINGS: Error message:', error?.message)
        console.error('📋 INTELLIGENCE SETTINGS: Error status:', error?.status)
        console.error('📋 INTELLIGENCE SETTINGS: Error response:', error?.response)
        throw error
      }
    },
    retry: (failureCount, error) => {
      console.log(`🔄 INTELLIGENCE SETTINGS: Retry attempt ${failureCount}`)
      console.log('🔄 INTELLIGENCE SETTINGS: Retry error:', error)
      return failureCount < 3
    },
    onSuccess: (data) => {
      console.log('🎉 INTELLIGENCE SETTINGS: onSuccess callback triggered')
      console.log('📊 INTELLIGENCE SETTINGS: Success data:', data)
      console.log('🔧 INTELLIGENCE SETTINGS: Setting local settings:', data.data)
      setLocalSettings(data.data)
    },
    onError: (error) => {
      console.error('💥 INTELLIGENCE SETTINGS: onError callback triggered')
      console.error('💥 INTELLIGENCE SETTINGS: Error in onError:', error)
      console.error('💥 INTELLIGENCE SETTINGS: Error details:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        stack: error?.stack,
        status: error?.status,
        response: error?.response
      })
    }
  })

  // Sync settingsData with localSettings when data is loaded
  useEffect(() => {
    if (settingsData?.data) {
      console.log('🔄 INTELLIGENCE SETTINGS: Syncing settingsData to localSettings')
      console.log('📊 INTELLIGENCE SETTINGS: settingsData.data:', settingsData.data)
      setLocalSettings(settingsData.data)
    }
  }, [settingsData]) // Only depend on settingsData changes

  // Log current state
  console.log('📊 INTELLIGENCE SETTINGS: Current query state:', {
    status,
    fetchStatus,
    isLoading,
    error: error?.message,
    hasData: !!settingsData,
    dataKeys: settingsData ? Object.keys(settingsData) : null,
    localSettings: localSettings
  })

  const updateSettingsMutation = useMutation({
    mutationFn: (data: Partial<IntelligenceSettingsData>) =>
      apiClient.post('/intelligence/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intelligence-settings'] })
      setHasChanges(false)
    }
  })

  const handleSettingChange = (section: keyof IntelligenceSettingsData, key: string, value: any) => {
    if (!localSettings) return

    const updatedSettings = {
      ...localSettings,
      [section]: {
        ...localSettings[section],
        [key]: value
      }
    }

    setLocalSettings(updatedSettings)
    setHasChanges(true)
  }

  const handleSaveSettings = () => {
    if (localSettings) {
      updateSettingsMutation.mutate(localSettings)
    }
  }

  const handleResetSettings = () => {
    if (settingsData?.data) {
      setLocalSettings(settingsData.data)
      setHasChanges(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !localSettings) {
    console.error('🚨 INTELLIGENCE SETTINGS: Rendering error state')
    console.error('🚨 INTELLIGENCE SETTINGS: Error object in render:', error)
    console.error('🚨 INTELLIGENCE SETTINGS: Error message:', error?.message)
    console.error('🚨 INTELLIGENCE SETTINGS: Error status:', error?.status)
    console.error('🚨 INTELLIGENCE SETTINGS: Local settings:', localSettings)

    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Failed to load intelligence settings</p>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
          <p className="text-sm text-red-700 font-medium">Debug Information:</p>
          <p className="text-xs text-red-600 mt-1">
            Status: {status} | Fetch: {fetchStatus}
          </p>
          <p className="text-xs text-red-600">
            Error: {error?.message || 'Unknown error'}
          </p>
          <p className="text-xs text-red-600">
            Local Settings: {localSettings ? 'Present' : 'Missing'}
          </p>
          <p className="text-xs text-red-600">
            Check browser console for detailed logs
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Cog6ToothIcon className="w-8 h-8 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Intelligence Settings</h2>
            <p className="text-gray-600">Configure Phase 2 intelligence layer parameters</p>
          </div>
        </div>

        {/* Save/Reset Controls */}
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center space-x-3"
          >
            <button
              onClick={handleResetSettings}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={updateSettingsMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </motion.div>
        )}
      </div>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Behavior Simulation Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <UserIcon className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Behavior Simulation</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Enable Behavior Simulation</label>
              <button
                onClick={() => handleSettingChange('behavior_simulation', 'enabled', !localSettings.behavior_simulation.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.behavior_simulation.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.behavior_simulation.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Profile</label>
              <select
                value={localSettings.behavior_simulation.default_profile}
                onChange={(e) => handleSettingChange('behavior_simulation', 'default_profile', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="technical">Technical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response Delay Multiplier: {localSettings.behavior_simulation.response_delay_multiplier}x
              </label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={localSettings.behavior_simulation.response_delay_multiplier}
                onChange={(e) => handleSettingChange('behavior_simulation', 'response_delay_multiplier', parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Faster</span>
                <span>Slower</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Humanization Level</label>
              <select
                value={localSettings.behavior_simulation.humanization_level}
                onChange={(e) => handleSettingChange('behavior_simulation', 'humanization_level', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Engagement Threshold: {Math.round(localSettings.behavior_simulation.engagement_threshold * 100)}%
              </label>
              <input
                type="range"
                min="0.3"
                max="0.9"
                step="0.05"
                value={localSettings.behavior_simulation.engagement_threshold}
                onChange={(e) => handleSettingChange('behavior_simulation', 'engagement_threshold', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </motion.div>

        {/* Spam Detection Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <ShieldCheckIcon className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Spam Detection</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Enable Spam Detection</label>
              <button
                onClick={() => handleSettingChange('spam_detection', 'enabled', !localSettings.spam_detection.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.spam_detection.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.spam_detection.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Detection Sensitivity</label>
              <select
                value={localSettings.spam_detection.sensitivity}
                onChange={(e) => handleSettingChange('spam_detection', 'sensitivity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="low">Low (Less Restrictive)</option>
                <option value="medium">Medium (Balanced)</option>
                <option value="high">High (More Restrictive)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-Block Threshold: {localSettings.spam_detection.auto_block_threshold}
              </label>
              <input
                type="range"
                min="60"
                max="100"
                step="5"
                value={localSettings.spam_detection.auto_block_threshold}
                onChange={(e) => handleSettingChange('spam_detection', 'auto_block_threshold', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manual Review Threshold: {localSettings.spam_detection.manual_review_threshold}
              </label>
              <input
                type="range"
                min="40"
                max="80"
                step="5"
                value={localSettings.spam_detection.manual_review_threshold}
                onChange={(e) => handleSettingChange('spam_detection', 'manual_review_threshold', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Learning Mode</label>
              <button
                onClick={() => handleSettingChange('spam_detection', 'learning_mode', !localSettings.spam_detection.learning_mode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.spam_detection.learning_mode ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.spam_detection.learning_mode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Crisis Management Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
            <h3 className="text-lg font-semibold text-gray-900">Crisis Management</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Enable Crisis Management</label>
              <button
                onClick={() => handleSettingChange('crisis_management', 'enabled', !localSettings.crisis_management.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.crisis_management.enabled ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.crisis_management.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Auto-Response</label>
              <button
                onClick={() => handleSettingChange('crisis_management', 'auto_response', !localSettings.crisis_management.auto_response)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.crisis_management.auto_response ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.crisis_management.auto_response ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalation Threshold: {localSettings.crisis_management.escalation_threshold}
              </label>
              <input
                type="range"
                min="50"
                max="90"
                step="5"
                value={localSettings.crisis_management.escalation_threshold}
                onChange={(e) => handleSettingChange('crisis_management', 'escalation_threshold', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notification Email</label>
              <input
                type="email"
                value={localSettings.crisis_management.notification_email}
                onChange={(e) => handleSettingChange('crisis_management', 'notification_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Response Timeout: {Math.round(localSettings.crisis_management.response_timeout / 60)} minutes
              </label>
              <input
                type="range"
                min="1800"
                max="14400"
                step="900"
                value={localSettings.crisis_management.response_timeout}
                onChange={(e) => handleSettingChange('crisis_management', 'response_timeout', parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </motion.div>

        {/* Performance Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <ClockIcon className="w-6 h-6 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Performance & Privacy</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Timeout: {localSettings.performance.api_timeout}s
              </label>
              <input
                type="range"
                min="15"
                max="60"
                step="5"
                value={localSettings.performance.api_timeout}
                onChange={(e) => handleSettingChange('performance', 'api_timeout', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Processing Batch Size: {localSettings.performance.batch_size}
              </label>
              <input
                type="range"
                min="5"
                max="25"
                step="5"
                value={localSettings.performance.batch_size}
                onChange={(e) => handleSettingChange('performance', 'batch_size', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Retention: {localSettings.privacy.data_retention_days} days
              </label>
              <input
                type="range"
                min="30"
                max="365"
                step="30"
                value={localSettings.privacy.data_retention_days}
                onChange={(e) => handleSettingChange('privacy', 'data_retention_days', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Anonymize Logs</label>
              <button
                onClick={() => handleSettingChange('privacy', 'anonymize_logs', !localSettings.privacy.anonymize_logs)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  localSettings.privacy.anonymize_logs ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    localSettings.privacy.anonymize_logs ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Success Message */}
      {updateSettingsMutation.isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-md p-4"
        >
          <div className="flex items-center">
            <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
            <p className="text-sm text-green-700">Settings updated successfully!</p>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default IntelligenceSettings